import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { VacationUsageSummary } from '../../types';
import { MemberCell } from './MemberCell';

interface TeamUsageTableProps {
  summaries: VacationUsageSummary[];
  loading?: boolean;
  /** Optional filter controls rendered above the table. */
  filters?: React.ReactNode;
  /** Optional footer (e.g. pagination) rendered below the table. */
  footer?: React.ReactNode;
}

/**
 * Colour thresholds for remaining days:
 *  - green  when >= 10
 *  - amber  when 4–9
 *  - red    when <= 3
 */
export const getRemainingColorClass = (remaining: number): string => {
  if (remaining >= 10) return 'text-green-600';
  if (remaining >= 4) return 'text-amber-600';
  return 'text-red-600';
};

export const TeamUsageTable: React.FC<TeamUsageTableProps> = ({
  summaries,
  loading,
  filters,
  footer,
}) => {
  const { t } = useTranslation('vacations');

  const days = (count: number): string => t('usage.days', { count });

  return (
    <Card data-testid="vacations-team-usage">
      <CardContent className="pt-6">
        {filters && <div className="mb-4">{filters}</div>}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('usage.headers.member')}</TableHead>
              <TableHead>{t('usage.headers.entitlement')}</TableHead>
              <TableHead>{t('usage.headers.used')}</TableHead>
              <TableHead>{t('usage.headers.remaining')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  {t('table.loading')}
                </TableCell>
              </TableRow>
            ) : summaries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  {t('usage.empty')}
                </TableCell>
              </TableRow>
            ) : (
              summaries.map((summary) => (
                <TableRow key={summary.memberId} data-testid={`usage-row-${summary.memberId}`}>
                  <TableCell>
                    <MemberCell name={summary.memberName} />
                  </TableCell>
                  <TableCell>{summary.annualAllowance != null ? days(summary.annualAllowance) : '-'}</TableCell>
                  <TableCell>{days(summary.usedDays)}</TableCell>
                  <TableCell>
                    <span
                      className={`font-semibold ${getRemainingColorClass(summary.available)}`}
                      data-testid={`usage-remaining-${summary.memberId}`}
                    >
                      {days(summary.available)}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {footer}
      </CardContent>
    </Card>
  );
};
